import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Divider,
  LinearProgress, Chip, Alert, Grid
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ReplayIcon from '@mui/icons-material/Replay';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SchoolIcon from '@mui/icons-material/School';

const QuizResults = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { moduleId, courseId } = useParams();

  if (!state) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 4, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>No results to display</Typography>
        <Typography color="text.secondary" gutterBottom>
          You need to take the quiz first.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate(`/module/${moduleId}/course/${courseId}/quiz`)}
          sx={{ mt: 2 }}
        >
          Go to Quiz
        </Button>
      </Box>
    );
  }

  const { quiz, answers, score, passed, courseTitle, moduleTitle, nextDestination } = state;
  const { questions = [], passingScore = 70 } = quiz;

  const correctCount = questions.filter((q, i) => answers[i] === q.answer).length;
  const wrongCount = questions.length - correctCount;
  const isPerfect = correctCount === questions.length;

  const headerBg = passed
    ? 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)'
    : 'linear-gradient(135deg, #b71c1c 0%, #e53935 100%)';

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>

      {/* Result banner */}
      <Paper
        elevation={6}
        sx={{
          background: headerBg,
          borderRadius: 4,
          p: 4,
          mb: 4,
          color: 'white',
          textAlign: 'center'
        }}
      >
        {isPerfect ? (
          <EmojiEventsIcon sx={{ fontSize: 72, mb: 1, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))' }} />
        ) : passed ? (
          <CheckCircleIcon sx={{ fontSize: 72, mb: 1 }} />
        ) : (
          <SchoolIcon sx={{ fontSize: 72, mb: 1, opacity: 0.9 }} />
        )}

        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {isPerfect ? 'Perfect Score!' : passed ? 'Quiz Passed!' : 'Not Quite Yet'}
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ opacity: 0.9 }}>
          {courseTitle}
        </Typography>

        <Box sx={{ maxWidth: 400, mx: 'auto', mt: 2, mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={score}
            sx={{
              height: 14,
              borderRadius: 7,
              bgcolor: 'rgba(255,255,255,0.25)',
              '& .MuiLinearProgress-bar': {
                bgcolor: passed ? '#a5d6a7' : '#ef9a9a',
                borderRadius: 7
              }
            }}
          />
        </Box>

        <Typography variant="h3" fontWeight="bold">
          {score}%
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.85, mt: 0.5 }}>
          {passed
            ? `Passed — required ${passingScore}%`
            : `${passingScore}% required to pass`}
        </Typography>
      </Paper>

      {/* Summary stats */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={4}>
          <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderRadius: 3, borderTop: '4px solid #4caf50' }}>
            <Typography variant="h4" fontWeight="bold" color="success.main">{correctCount}</Typography>
            <Typography variant="body2" color="text.secondary">Correct</Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderRadius: 3, borderTop: '4px solid #f44336' }}>
            <Typography variant="h4" fontWeight="bold" color="error.main">{wrongCount}</Typography>
            <Typography variant="body2" color="text.secondary">Incorrect</Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderRadius: 3, borderTop: '4px solid #1976d2' }}>
            <Typography variant="h4" fontWeight="bold" color="primary.main">{questions.length}</Typography>
            <Typography variant="body2" color="text.secondary">Total</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Perfect score callout */}
      {isPerfect && (
        <Alert
          severity="success"
          icon={<EmojiEventsIcon />}
          sx={{ mb: 3, fontSize: '1rem', fontWeight: 'bold' }}
        >
          You answered every question correctly — outstanding work!
        </Alert>
      )}

      {/* All correct callout when not perfect but passed */}
      {!isPerfect && passed && wrongCount > 0 && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Great job! You passed. Review the {wrongCount} incorrect answer{wrongCount > 1 ? 's' : ''} below to strengthen your understanding.
        </Alert>
      )}

      {/* Failed callout */}
      {!passed && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You need {passingScore}% to unlock the next course. Review the explanations below and try again — you can retake it as many times as needed.
        </Alert>
      )}

      {/* Per-question breakdown */}
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Question Breakdown
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {questions.map((q, i) => {
        const userAnswer = answers[i];
        const isCorrect = userAnswer === q.answer;

        return (
          <Paper
            key={i}
            elevation={2}
            sx={{
              mb: 2,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: isCorrect ? 'success.200' : 'error.200'
            }}
          >
            {/* Question header */}
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                bgcolor: isCorrect ? '#f0fdf4' : '#fff5f5',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
              }}
            >
              {isCorrect
                ? <CheckCircleIcon color="success" />
                : <CancelIcon color="error" />
              }
              <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
                {i + 1}. {q.question}
              </Typography>
              <Chip
                label={isCorrect ? 'Correct' : 'Incorrect'}
                color={isCorrect ? 'success' : 'error'}
                size="small"
                variant="filled"
              />
            </Box>

            {/* Answer details */}
            <Box sx={{ px: 2.5, py: 2 }}>
              {isCorrect ? (
                <Typography variant="body2" color="success.main" fontWeight="bold">
                  Your answer: {userAnswer}
                </Typography>
              ) : (
                <Box>
                  <Box
                    sx={{
                      display: 'flex', alignItems: 'flex-start', gap: 1,
                      bgcolor: '#ffebee', borderRadius: 1, px: 1.5, py: 1, mb: 1
                    }}
                  >
                    <CancelIcon color="error" fontSize="small" sx={{ mt: 0.2, flexShrink: 0 }} />
                    <Typography variant="body2">
                      <strong>Your answer:</strong> {userAnswer || 'No answer selected'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex', alignItems: 'flex-start', gap: 1,
                      bgcolor: '#e8f5e9', borderRadius: 1, px: 1.5, py: 1, mb: 1
                    }}
                  >
                    <CheckCircleIcon color="success" fontSize="small" sx={{ mt: 0.2, flexShrink: 0 }} />
                    <Typography variant="body2">
                      <strong>Correct answer:</strong> {q.answer}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Explanation — shown for all questions */}
              {q.explanation && (
                <Box
                  sx={{
                    mt: 1.5, px: 1.5, py: 1,
                    bgcolor: '#f5f5f5', borderRadius: 1,
                    borderLeft: '3px solid #90a4ae'
                  }}
                >
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    {q.explanation}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        );
      })}

      {/* Action buttons */}
      <Box
        sx={{
          mt: 4, pt: 3,
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ReplayIcon />}
          onClick={() => navigate(`/module/${moduleId}/course/${courseId}/quiz`)}
        >
          {passed ? 'Retake Quiz' : 'Try Again'}
        </Button>

        <Button
          variant="outlined"
          onClick={() => navigate(`/module/${moduleId}/course/${courseId}`)}
        >
          Back to Lessons
        </Button>

        {passed && (
          <Button
            variant="contained"
            color="success"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate(nextDestination)}
            sx={{ px: 4 }}
          >
            Continue
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default QuizResults;
